import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, X, ArrowRight, Heart, Sparkles, ShieldCheck } from 'lucide-react';

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    subtitle: 'Reader',
    price: 'Free forever',
    gradient: 'from-slate-100 to-slate-50',
    border: 'border-slate-200',
    btnClass: 'bg-slate-800 hover:bg-slate-900 text-white',
    icon: <Heart className="w-6 h-6 text-slate-600" />,
    features: [
      { text: '1 digest profile', on: true },
      { text: 'Personalized specialty digests', on: true },
      { text: 'Literature search & exploration', on: true },
      { text: 'Personal library (100 articles)', on: true },
      { text: 'Reading progress & notes', on: true },
      { text: '1 Run Now request / 24h', on: true },
      { text: 'Community — read only', on: true },
      { text: 'Community posting & commenting', on: false },
      { text: 'Audio digests & summaries', on: false },
      { text: 'LitScholar AI deep-dive', on: false },
      { text: 'Library export (CSV & RIS)', on: false },
    ],
    cta: { label: 'Get Started', href: '/signup' },
  },
  {
    id: 'verified',
    name: 'Free + Verified',
    subtitle: 'Verified Clinician',
    price: 'Free — verify credentials',
    gradient: 'from-emerald-50 to-teal-50',
    border: 'border-emerald-200',
    btnClass: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    icon: <ShieldCheck className="w-6 h-6 text-emerald-600" />,
    badge: 'Most trusted',
    features: [
      { text: '3 digest profiles', on: true },
      { text: 'Personalized specialty digests', on: true },
      { text: 'Literature search & exploration', on: true },
      { text: 'Personal library (100 articles)', on: true },
      { text: 'Reading progress & notes', on: true },
      { text: '1 Run Now request / 24h', on: true },
      { text: 'Community — full access', on: true },
      { text: 'Community posting & commenting', on: true },
      { text: 'Audio digests & summaries', on: false },
      { text: 'LitScholar AI deep-dive', on: false },
      { text: 'Library export (CSV & RIS)', on: false },
    ],
    cta: { label: 'Verify Now', href: '/verification' },
  },
  {
    id: 'pro',
    name: 'Pro',
    subtitle: 'Full Access',
    price: 'Coming soon',
    gradient: 'from-sky-50 to-indigo-50',
    border: 'border-sky-300',
    btnClass: 'bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 text-white',
    icon: <Sparkles className="w-6 h-6 text-sky-600" />,
    highlight: true,
    features: [
      { text: '5 digest profiles', on: true },
      { text: 'Personalized specialty digests', on: true },
      { text: 'Literature search & exploration', on: true },
      { text: 'Personal library (1,000 articles)', on: true },
      { text: 'Reading progress & notes', on: true },
      { text: '5 Run Now requests / 24h', on: true },
      { text: 'Community — full access', on: true },
      { text: 'Community posting & commenting', on: true, note: 'Requires clinician verification' },
      { text: 'Audio digests & summaries', on: true },
      { text: 'LitScholar AI deep-dive', on: true },
      { text: 'Library export (CSV & RIS)', on: true },
    ],
    cta: { label: 'Subscribe', href: '/plan' },
  },
];

const MembershipPage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-sky-400 to-cyan-500 rounded-lg flex items-center justify-center">
              <Heart className="w-4 h-4 text-white fill-white" />
            </div>
            <span className="text-lg font-bold bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-transparent">
              LitCurrent
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Membership Plans</h1>
          <p className="text-slate-500 text-lg max-w-xl mx-auto">
            Choose the plan that fits your workflow. All plans include personalized digests.
          </p>
        </div>

        {/* Trial banner */}
        <div className="bg-gradient-to-r from-sky-50 via-indigo-50 to-violet-50 border border-sky-200 rounded-2xl p-5 mb-10 text-center">
          <p className="text-base font-semibold text-sky-800">
            All new users get a <strong>30-day Pro trial</strong> with full access — including community posting.
          </p>
          <p className="text-sm text-sky-600 mt-1">No credit card required. Starts automatically at signup.</p>
        </div>

        {/* Tiers */}
        <div className="grid md:grid-cols-3 gap-5 mb-12">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-gradient-to-b ${tier.gradient} rounded-2xl border ${tier.border} p-6 flex flex-col ${tier.highlight ? 'ring-2 ring-sky-300 shadow-lg shadow-sky-100/50' : 'shadow-sm'}`}
            >
              {tier.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                    {tier.badge}
                  </span>
                </div>
              )}
              {tier.highlight && (
                <div className="absolute -top-3 right-4">
                  <span className="bg-sky-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                    Recommended
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-white/80 rounded-xl shadow-sm">{tier.icon}</div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">{tier.name}</h3>
                  <p className="text-xs text-slate-500">{tier.subtitle}</p>
                </div>
              </div>
              <p className="text-sm font-semibold text-slate-700 mb-5">{tier.price}</p>

              <ul className="space-y-2.5 flex-1 mb-6">
                {tier.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    {f.on ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />
                    )}
                    <span className={f.on ? 'text-slate-700' : 'text-slate-400'}>
                      {f.text}
                      {f.note && <span className="text-xs text-slate-400 ml-1">({f.note})</span>}
                    </span>
                  </li>
                ))}
              </ul>

              <Link
                to={user ? tier.cta.href : tier.cta.href}
                className={`block w-full text-center py-2.5 rounded-xl font-semibold text-sm transition-all ${tier.btnClass}`}
              >
                {tier.cta.label} <ArrowRight className="w-4 h-4 inline ml-1" />
              </Link>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div className="text-center text-sm text-slate-400">
          <p>Community posting always requires clinician verification (work email or NPI), regardless of plan.</p>
          {!user && (
            <p className="mt-2">
              Already have an account?{' '}
              <Link to="/login" className="text-sky-600 hover:underline">Sign in</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
